import { useAuthRedirect } from '@manager/composables/use-auth-redirect';
import { useCan } from '@manager/composables/use-can';
import { useManagerFeature } from '@manager/composables/use-manager-feature';
import { useAuthStore } from '@manager/store/auth-store';
import { isEqual, pick } from 'lodash-es';
import { getEnv, toArray } from 'shared/utils';
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
} from 'vue-router';

import { routeNames } from './route-names';
import routes from './routes';

function isSamePage(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) {
  const pageRecord = to.matched[0];
  if (pageRecord !== from.matched[0]) {
    return false;
  }
  // Only the page record's own params: modal child routes can add params of their own.
  const pageParamNames = [...pageRecord.path.matchAll(/:(\w+)/g)].map(
    (match) => match[1]
  );
  return isEqual(
    pick(to.params, pageParamNames),
    pick(from.params, pageParamNames)
  );
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    // Opening, closing or navigating inside a modal keeps the page underneath where it is.
    const isModalNavigation = !!to.meta.modal || !!from.meta.modal;
    if (isModalNavigation && isSamePage(to, from)) {
      return false;
    }
    return { top: 0 };
  },
});

export function getDefaultLandingPage() {
  return routeNames.tasksOverview;
}

// handle authorization and permissions
router.beforeEach(async (to, _, next) => {
  const { can } = useCan();
  const authStore = useAuthStore();
  const isAuthenticated = authStore.isAuthenticated;
  const { isFeatureDisabled } = useManagerFeature();

  if (to.meta?.env) {
    const envs = toArray(to.meta.env);

    if (!envs.includes(getEnv())) {
      return next({ name: routeNames.forbidden });
    }
  }

  if (to.fullPath === '/' && !isAuthenticated) {
    return next({ name: routeNames.login });
  }
  if (to.fullPath === '/' && isAuthenticated) {
    return next({ name: getDefaultLandingPage() });
  }
  if (to.name === routeNames.login && isAuthenticated) {
    return next({ name: getDefaultLandingPage() });
  }

  // A route needs authentication if it explicitly requires it, or if it
  // carries any protection meta that would be silently skipped otherwise.
  const needsAuth =
    to.meta?.requiresAuth ||
    to.meta?.permission != null ||
    to.meta?.feature != null ||
    to.meta?.additionalCheck != null;

  if (!needsAuth) {
    return next();
  }

  if (!isAuthenticated) {
    const { saveRedirectUrl } = useAuthRedirect();
    saveRedirectUrl(to.fullPath);
    return next({ name: routeNames.login });
  }

  if (
    to.meta?.additionalCheck &&
    authStore.user &&
    !to.meta.additionalCheck(authStore.user)
  ) {
    return next({ name: routeNames.forbidden });
  }

  if (to.meta?.permission != null) {
    const canOne = toArray(to.meta.permission).some((permission) =>
      can(permission)
    );
    if (!canOne) {
      return next({ name: routeNames.forbidden });
    }
  }

  if (to.meta?.feature != null && isFeatureDisabled(to.meta.feature)) {
    return next({ name: routeNames.forbidden });
  }

  next();
});

export default router;
